import { useMutation, useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api-context'

/**
 * O cadastro pelo link da clínica (docs/api/link-de-cadastro.md; docs/areas/pacientes.md RN-PAC-31 a
 * 35 e 42; RN-POR-18). Sem sessão: o token do link vai em `?t=` em toda chamada. A página nunca chama
 * `fetch`.
 */

export interface Opening {
  clinic: { name: string; city: string | null; state: string | null; logoUrl: string | null }
  privacyNotice: string
}

export type Gender = 'female' | 'male' | 'other' | 'not_provided'
export type MaritalStatus =
  'single' | 'married' | 'domestic_partnership' | 'separated' | 'divorced' | 'widowed'
export type ForeignDocumentType = 'passport' | 'rne' | 'crnm' | 'other'
export type ReferralChannel =
  | 'referral'
  | 'social_media'
  | 'instagram'
  | 'google'
  | 'ad'
  | 'website'
  | 'storefront'
  | 'event'
  | 'other'

export interface AddressInput {
  postalCode: string
  street: string
  number: string
  complement?: string
  district: string
  city: string
  state: string
  country: string
}

export interface GuardianInput {
  name: string
  docNumber: string
  whatsapp: string
}

/** O corpo de `POST /registration`: só nome, WhatsApp e o aceite são obrigatórios (RN-PAC-33). */
export interface RegisterInput {
  name: string
  whatsapp: string
  dialCode?: string
  email?: string
  birthDate?: string
  docNumber?: string
  foreignDocument?: { type: ForeignDocumentType; number: string; country: string }
  rg?: string
  gender?: Gender
  maritalStatus?: MaritalStatus
  occupation?: string
  address?: AddressInput
  guardian?: GuardianInput
  instagram?: string
  referralSource?: { channel: ReferralChannel; detail?: string }
  privacyAccepted: true
  /** As confirmações do preenchimento rápido e da troca de e-mail (RN-PAC-53, RN-PAC-55). */
  identity?: { prefillId: string; emailGrantId?: string }
}

export interface Registered {
  patient: { firstName: string; email: string | null }
  clinic: { name: string }
  portalUrl: string
}

/** `GET /registration/postal-code/:postalCode`: o que a consulta de CEP preenche. */
export interface PostalCodeAddress {
  postalCode: string
  street: string | null
  district: string | null
  city: string
  state: string
}

export const registrationKeys = {
  opening: (token: string) => ['registration', token] as const,
}

export function useOpening(token: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: registrationKeys.opening(token ?? ''),
    queryFn: () => api.get<Opening>('/registration', { t: token }),
    enabled: Boolean(token),
    retry: false,
  })
}

export function useRegister(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: RegisterInput) =>
      api.post<Registered>(`/registration?t=${encodeURIComponent(token ?? '')}`, input),
  })
}

/** A consulta de CEP do formulário (P-PAC-55): um atalho; a falha vira dica e o endereço segue à mão. */
export function usePostalCode(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: (code: string) =>
      api.get<PostalCodeAddress>(`/registration/postal-code/${code}`, { t: token }),
  })
}
